"use client";

import React, { useEffect, useRef, useState } from "react";

interface AnimatedCounterProps {
  target: number;
  suffix?: string;
}

const formatCounterValue = (target: number, suffix: string) =>
  target % 1 !== 0
    ? target.toFixed(1) + suffix
    : target >= 1000
      ? Math.round(target).toLocaleString() + suffix
      : target + suffix;

export function AnimatedCounter({ target, suffix = "" }: AnimatedCounterProps) {
  // Render the real value in SSR and before the section enters the viewport.
  // The count-up remains a progressive visual enhancement, not the content.
  const [displayValue, setDisplayValue] = useState<string>(() => formatCounterValue(target, suffix));
  const elementRef = useRef<HTMLSpanElement>(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      setDisplayValue(formatCounterValue(target, suffix));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animatedRef.current) {
            animatedRef.current = true;
            observer.unobserve(entry.target);

            let startTime: number | null = null;
            const duration = 1600;

            const tick = (timestamp: number) => {
              if (!startTime) startTime = timestamp;
              const progress = Math.min((timestamp - startTime) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const currentVal = target * eased;

              if (target % 1 !== 0) {
                setDisplayValue(currentVal.toFixed(1) + suffix);
              } else if (currentVal >= 1000) {
                setDisplayValue(Math.round(currentVal).toLocaleString() + suffix);
              } else {
                setDisplayValue(Math.round(currentVal) + suffix);
              }

              if (progress < 1) {
                requestAnimationFrame(tick);
              } else {
                setDisplayValue(formatCounterValue(target, suffix));
              }
            };

            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix]);

  return (
    <strong ref={elementRef} data-count={target} data-suffix={suffix}>
      {displayValue}
    </strong>
  );
}
